/**
 * App credentials: the permissions, entity annotations and API types shared by
 * the backend plugin (`@internal/backstage-plugin-app-credentials-backend`), the
 * backend's permission policy and the "Credentials" entity card.
 *
 * @packageDocumentation
 */

import type { Entity } from '@backstage/catalog-model';
import { RESOURCE_TYPE_CATALOG_ENTITY } from '@backstage/plugin-catalog-common/alpha';
import { createPermission } from '@backstage/plugin-permission-common';

/** The backend plugin's ID: its routes are under `/api/app-credentials`. */
export const APP_CREDENTIALS_PLUGIN_ID = 'app-credentials';

/**
 * Reveal an app's Keycloak client secret. Resource: the app's catalog entity,
 * so the policy can limit it to the entity's owners.
 */
export const appCredentialsSecretReadPermission = createPermission({
  name: 'app-credentials.secret.read',
  attributes: { action: 'read' },
  resourceType: RESOURCE_TYPE_CATALOG_ENTITY,
});

/**
 * Regenerate an app's Keycloak client secret (the old one stops working
 * immediately). Resource: the app's catalog entity.
 */
export const appCredentialsSecretRegeneratePermission = createPermission({
  name: 'app-credentials.secret.regenerate',
  attributes: { action: 'update' },
  resourceType: RESOURCE_TYPE_CATALOG_ENTITY,
});

/** All permissions of the app-credentials plugin. */
export const appCredentialsPermissions = [
  appCredentialsSecretReadPermission,
  appCredentialsSecretRegeneratePermission,
];

/**
 * Annotations set by the keycloak-apps catalog module
 * (`plugins/catalog-backend-module-keycloak-apps`) on each app's Resource.
 */
export const ANNOTATION_KEYCLOAK_CLIENT_ID =
  'pubinvest.co.uk/keycloak-client-id';
export const ANNOTATION_KEYCLOAK_CLIENT_TYPE =
  'pubinvest.co.uk/keycloak-client-type';
export const ANNOTATION_KEYCLOAK_ENV = 'pubinvest.co.uk/keycloak-env';
export const ANNOTATION_KEYCLOAK_APP_NAME = 'pubinvest.co.uk/keycloak-app-name';

/** keycloak-config client types. Only `public` clients have no secret. */
export type KeycloakClientType = 'confidential' | 'public' | 'service' | string;

/** The Keycloak client of an app entity, from its annotations. */
export interface KeycloakAppClient {
  clientId: string;
  type?: KeycloakClientType;
  env?: string;
  appName?: string;
}

/**
 * The Keycloak client of a keycloak-config app entity, or undefined when the
 * entity has no `pubinvest.co.uk/keycloak-client-id` annotation.
 */
export function keycloakAppClient(
  entity: Entity,
): KeycloakAppClient | undefined {
  const a = entity.metadata.annotations ?? {};
  const clientId = a[ANNOTATION_KEYCLOAK_CLIENT_ID]?.trim();
  if (!clientId) return undefined;
  return {
    clientId,
    type: a[ANNOTATION_KEYCLOAK_CLIENT_TYPE] || undefined,
    env: a[ANNOTATION_KEYCLOAK_ENV] || undefined,
    appName: a[ANNOTATION_KEYCLOAK_APP_NAME] || undefined,
  };
}

/** Whether the client type has no secret (keycloak-config `public`). */
export function isPublicClient(type: KeycloakClientType | undefined): boolean {
  return type === 'public';
}

/** `GET /api/app-credentials/status`. */
export interface AppCredentialsStatus {
  /** False when `appCredentials.keycloak` isn't configured: the routes answer 503. */
  configured: boolean;
  /** The Keycloak realm's issuer URL, when configured. */
  issuer?: string;
  /**
   * The keycloak-config environments (`apps/<env>/`) whose secrets this
   * Backstage serves; undefined means all.
   */
  environments?: string[];
}

/**
 * `GET /api/app-credentials/secret?entityRef=...` and
 * `POST /api/app-credentials/regenerate` (body `{entityRef}`).
 */
export interface AppCredentialsSecret {
  entityRef: string;
  clientId: string;
  secret: string;
}

/** Error names in the routes' error bodies (`{error: {name, message}}`). */
export const APP_CREDENTIALS_ERRORS = {
  /** 409: the client is public (or bearer-only) and has no secret. */
  noSecret: 'NoSecretError',
  /** 503: `appCredentials.keycloak` isn't configured. */
  notConfigured: 'NotConfiguredError',
} as const;
